import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/cn';

/**
 * Invite — public referral landing page (`/invite`).
 *
 * Reached via the affiliate/referral share link
 * `https://app.olliereads.org/invite?ref=<code>`. The `?ref=<code>` capture
 * is NOT handled here — it already runs at boot in `main.tsx`
 * (`captureReferralFromUrl()`), BEFORE the router mounts, persisting the
 * code to localStorage with a 30-day attribution window and stripping the
 * param while preserving the `/invite` pathname. By the time this component
 * mounts the ref is already stored; the later sign-up flow reads it back on
 * `POST /auth/callback` via `getStoredReferralCode()`.
 *
 * This is a standalone, full-bleed marketing page (no AppShell / app chrome),
 * mirroring the structure of `Login.tsx`. Its content is a 1:1 web port of
 * the `website/emails/ollie_is_live.html` welcome email — same copy, palette,
 * and typography — with two intentional differences:
 *   1. The hero eyebrow reads "You've been invited!" (email: "Ollie Reads is Live!").
 *   2. The "Check It Out" CTA links to the in-app `/login` route.
 *
 * The brand palette here is taken verbatim from the email's inline styles
 * (warm tan / terracotta), which is distinct from the app's orange-forward
 * `or-*` design tokens — so colours are applied as literal hex values and
 * the Lora/Inter fonts (loaded via `client/index.html`) are set inline.
 */

const SERIF = "'Lora', Georgia, 'Times New Roman', serif";
const SANS = "'Inter', -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

const STEPS = [
  {
    title: 'Add a reader.',
    body: 'Start with one and add more later.',
  },
  {
    title: 'Set their reading level.',
    body: 'We tailor the whole library to the readers reading level.',
  },
  {
    title: 'Start exploring.',
    body: 'Browse exciting new books, vocabulary bookmarks, quizzes, rewards, and more!',
  },
];

const FEATURES = [
  {
    title: 'A hand-curated library',
    body: 'Every title vetted for content, character, and quality.',
  },
  {
    title: 'Ollie Learns',
    body: 'History, science, and news, all in short reads and a quiz at the end',
  },
  {
    title: 'Quizzes & Reading Trackers',
    body: 'Check comprehension and log every book read this year.',
  },
  {
    title: 'Rewards & hatchery',
    body: 'Earn coins, hatch eggs, and collect creatures for completing quizzes.',
  },
];

// `?action=signup` is a LoginPage deep-link: it skips the marketing card and
// auto-fires Auth0 in create-account mode (see pages/Login.tsx). The captured
// referral already lives in localStorage, so it still rides along to
// POST /auth/callback after the OAuth round-trip.
const SIGNUP_HREF = '/login?action=signup';

function CheckItOutButton({ className }: { className?: string }) {
  return (
    <Link
      to={SIGNUP_HREF}
      className={cn(
        'inline-flex items-center justify-center rounded-full bg-[#c97b5c] px-9 py-[15px] text-[16px] font-semibold text-[#fffdf8] transition-[filter] hover:brightness-105',
        className,
      )}
    >
      Check It Out&nbsp;&rarr;
    </Link>
  );
}

export function InvitePage() {
  useEffect(() => {
    const previous = document.title;
    document.title = "You've been invited! · Ollie Reads";
    return () => {
      document.title = previous;
    };
  }, []);

  return (
    <div
      className="min-h-screen w-full bg-[#ece0cb] px-3 py-7 sm:py-10"
      style={{ fontFamily: SANS }}
    >
      <div className="mx-auto w-full max-w-[600px]">
        {/* Logo / brand header */}
        <div className="flex justify-center pb-5 pt-2">
          <a href="https://olliereads.org" target="_blank" rel="noreferrer">
            <img
              src="/brand/olliereads_logo.png"
              alt="Ollie Reads"
              className="block h-auto w-[148px]"
            />
          </a>
        </div>

        {/* Card */}
        <div className="overflow-hidden rounded-[18px] border border-[#e6d9c2] bg-[#fffdf8]">
          {/* Accent band */}
          <div className="h-1.5 bg-[#c97b5c]" />

          {/* Hero */}
          <div className="px-6 pt-10 sm:px-12">
            <p
              className="m-0 mb-3.5 text-[12px] font-semibold uppercase tracking-[0.14em] text-[#a85f44]"
            >
              You've been invited!
            </p>
            <h1
              className="m-0 mb-4 text-[30px] font-semibold leading-[1.12] tracking-[-0.01em] text-[#2a2520] sm:text-[34px]"
              style={{ fontFamily: SERIF }}
            >
              A reading world your
              <br />
              child will love.
            </h1>
            <p className="m-0 text-[16px] leading-[1.65] text-[#4a4338]">
              Hi there, welcome to <strong className="text-[#2a2520]">Ollie Reads</strong> — a
              safe, ad-free corner of the internet where kids discover hand-picked books, sorted by
              reading level to unlock a love for literature.
            </p>
            <h3
              className="mb-0 mt-6 text-[22px] font-semibold leading-[1.12] tracking-[-0.01em] text-[#2a2520] sm:text-[24px]"
              style={{ fontFamily: SERIF }}
            >
              Enjoy 3 Days of PREMIUM ACCESS for Free!
            </h3>
            <p className="m-0 text-[16px] leading-[1.65] text-[#4a4338]">(no card required)</p>
          </div>

          {/* Primary CTA */}
          <div className="px-6 pb-2 pt-5 sm:px-12">
            <CheckItOutButton />
          </div>

          {/* App preview */}
          <div className="px-6 pt-7 sm:px-12">
            <img
              src="/brand/app-book-library.png"
              alt="The Ollie Reads library of hand-picked children's books"
              className="block h-auto w-full max-w-[504px] rounded-xl border border-[#e6d9c2]"
            />
          </div>

          {/* Divider */}
          <div className="px-6 pt-6 sm:px-12">
            <div className="border-t border-[#ece0cb]" />
          </div>

          {/* Getting started */}
          <div className="px-6 pt-7 sm:px-12">
            <h2
              className="m-0 mb-1.5 text-[21px] font-semibold text-[#2a2520]"
              style={{ fontFamily: SERIF }}
            >
              Three steps to get started
            </h2>
            <ol className="m-0 list-none space-y-3.5 p-0 pt-3.5">
              {STEPS.map((step, i) => (
                <li key={step.title} className="flex items-start gap-2.5">
                  <span
                    aria-hidden
                    className="mt-0.5 flex h-[30px] w-[30px] flex-none items-center justify-center rounded-full bg-[#8a9a7b] text-[14px] font-bold text-white"
                  >
                    {i + 1}
                  </span>
                  <p className="m-0 text-[15px] leading-[1.6] text-[#4a4338]">
                    <strong className="text-[#2a2520]">{step.title}</strong> {step.body}
                  </p>
                </li>
              ))}
            </ol>
          </div>

          {/* Divider */}
          <div className="px-6 pt-7 sm:px-12">
            <div className="border-t border-[#ece0cb]" />
          </div>

          {/* What's inside */}
          <div className="px-6 pt-6 sm:px-12">
            <h2
              className="m-0 mb-1 text-[21px] font-semibold text-[#2a2520]"
              style={{ fontFamily: SERIF }}
            >
              What's inside
            </h2>
            <div className="grid grid-cols-1 gap-x-5 gap-y-4 pt-3.5 sm:grid-cols-2">
              {FEATURES.map((feature) => (
                <div key={feature.title}>
                  <p className="m-0 mb-0.5 text-[15px] font-bold text-[#2a2520]">{feature.title}</p>
                  <p className="m-0 text-[13.5px] leading-[1.55] text-[#7a7066]">{feature.body}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Sign off */}
          <div className="px-6 pb-10 pt-7 sm:px-12">
            <p className="m-0 mb-1 text-[15px] leading-[1.6] text-[#4a4338]">Happy reading,</p>
            <p
              className="m-0 text-[18px] font-semibold text-[#2a2520]"
              style={{ fontFamily: SERIF }}
            >
              Keri &amp; the Ollie Reads team
            </p>
            <CheckItOutButton className="mt-6" />
          </div>
        </div>

        {/* Help note */}
        <p className="px-6 pt-6 text-center text-[13.5px] leading-[1.6] text-[#7a7066] sm:px-12">
          Need a hand getting started? Reach us at{' '}
          <a href="mailto:hello@olliereads.org" className="text-[#a85f44] underline">
            hello@olliereads.org
          </a>
          .
        </p>

        {/* Footer */}
        <div className="px-6 pb-2 pt-5 text-center sm:px-12">
          <p className="m-0 mb-2.5 text-[12px] leading-[1.6] text-[#9a8f80]">
            <a href="https://olliereads.org" className="text-[#7a7066]">
              Ollie&nbsp;Reads
            </a>
            {' • '}
            <a href="https://olliereads.org/how-we-choose-books/" className="text-[#7a7066]">
              How&nbsp;we&nbsp;choose&nbsp;books
            </a>
            {' • '}
            <a href="https://olliereads.org/privacy-policy/" className="text-[#7a7066]">
              Privacy
            </a>
            {' • '}
            <a href="https://olliereads.org/terms-and-conditions/" className="text-[#7a7066]">
              Terms
            </a>
          </p>
          <p className="m-0 text-[12px] leading-[1.6] text-[#9a8f80]">&copy; 2026 Ollie Reads</p>
        </div>
      </div>
    </div>
  );
}
